'use client'

import { useState } from 'react'
import { Reveal } from '@/components/motion-primitives'
import { CtaButton } from '@/components/cta-button'
import { cn } from '@/lib/utils'

const SNIPPETS: Record<string, string> = {
  'Node.js': `import { Rombex } from "@rombex/node";

const rombex = new Rombex(process.env.ROMBEX_API_KEY);

// Send a cross-border payout
const payout = await rombex.payouts.create({
  amount: "1500.00",
  source: "USD",
  destination: "NGN",
  rail: "auto",
  beneficiary: "bnf_9f2a1c",
});

console.log(payout.status); // "settled"`,
  Python: `from rombex import Rombex

rombex = Rombex(api_key=os.environ["ROMBEX_API_KEY"])

payout = rombex.payouts.create(
    amount="1500.00",
    source="USD",
    destination="NGN",
    rail="auto",
    beneficiary="bnf_9f2a1c",
)

print(payout.status)  # "settled"`,
  PHP: `<?php
use Rombex\\Client;

$rombex = new Client(getenv('ROMBEX_API_KEY'));

$payout = $rombex->payouts->create([
  'amount' => '1500.00',
  'source' => 'USD',
  'destination' => 'NGN',
  'rail' => 'auto',
  'beneficiary' => 'bnf_9f2a1c',
]);

echo $payout->status; // "settled"`,
  Go: `package main

import "github.com/rombex/rombex-go"

func main() {
    client := rombex.New(os.Getenv("ROMBEX_API_KEY"))

    payout, _ := client.Payouts.Create(&rombex.PayoutParams{
        Amount:      "1500.00",
        Source:      "USD",
        Destination: "NGN",
        Rail:        "auto",
        Beneficiary: "bnf_9f2a1c",
    })

    fmt.Println(payout.Status) // "settled"
}`,
  Java: `import com.rombex.Rombex;

Rombex rombex = new Rombex(System.getenv("ROMBEX_API_KEY"));

Payout payout = rombex.payouts().create(
    PayoutParams.builder()
        .amount("1500.00")
        .source("USD")
        .destination("NGN")
        .rail("auto")
        .beneficiary("bnf_9f2a1c")
        .build());

System.out.println(payout.getStatus()); // "settled"`,
  JavaScript: `const res = await fetch("https://api.rombex.com/v1/payouts", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${ROMBEX_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    amount: "1500.00",
    source: "USD",
    destination: "NGN",
    rail: "auto",
    beneficiary: "bnf_9f2a1c",
  }),
});

const payout = await res.json();`,
}

const LANGS = Object.keys(SNIPPETS)

export function Developer() {
  const [lang, setLang] = useState('Node.js')

  return (
    <section className="bg-background py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-wider text-brand">Developers</p>
          <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Built for developers, first
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            A clean REST API and SDKs for every stack. Provision wallets, move money and reconcile
            payments with a few lines of code. Test everything in a sandbox before you go live.
          </p>
          <ul className="mt-6 space-y-3">
            {['REST API with predictable responses', 'SDKs for Node, Python, PHP, Go & Java', 'Webhooks for real-time events', 'Full sandbox environment'].map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm text-foreground">
                <span className="flex size-5 items-center justify-center rounded-full bg-brand/10 text-brand">
                  <svg viewBox="0 0 20 20" className="size-3" fill="currentColor">
                    <path d="M8 13.2 4.8 10l-1.1 1.1L8 15.4l8.3-8.3-1.1-1.1z" />
                  </svg>
                </span>
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <CtaButton href="https://merchant.rombex.com/api-docs" arrow external>
              Read Docs
            </CtaButton>
            <CtaButton href="https://merchant.rombex.com/api-docs" variant="secondary" external>
              View API
            </CtaButton>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="group overflow-hidden rounded-2xl border border-border shadow-2xl">
            <div className="relative bg-slate-950">
              <img
                src="/api-docs-screenshot.jpg"
                alt="Rombex API Documentation"
                className="h-auto w-full transition-transform duration-500 group-hover:scale-105"
              />
              <div className="pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_0_40px_rgba(0,0,0,0.2)]" />
              <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-slate-950 via-transparent to-transparent p-6">
                <a
                  href="https://merchant.rombex.com/api-docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg bg-brand px-6 py-3 font-semibold text-brand-foreground transition-all hover:shadow-lg hover:shadow-brand/30"
                >
                  View Full Documentation
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
